import LaunchIconRounded from "@mui/icons-material/Launch";
import { Box, Divider, Grid, Typography } from "@mui/material";

import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { IntegrationsMapping } from "../../../stack/integrations/IntegrationsMapping";
import SeverityError from "../../controls/components/ControlStatChart/images/SeverityError";
import SeveritySuccess from "../../controls/components/ControlStatChart/images/SeveritySuccess";
import SeverityWarning from "../../controls/components/ControlStatChart/images/SeverityWarning";
import SeverityWarningLight from "../../controls/components/ControlStatChart/images/SeverityWarningLight";
import CustomLabel from "../../frameworks/components/CustomLabel";
import PercentageLabel from "../../frameworks/components/PercentageLabel";

const TechniqueTooltipContent = ({ technique, showRisk }) => {
  const colors = useColors();
  const getCompliantIcon = (percentage) => {
    if (percentage === 100) return <SeveritySuccess width={"12px"} />;
    if (percentage >= 70) return <SeverityWarningLight width={"12px"} />;
    if (percentage > 30) return <SeverityWarning width={"12px"} />;
    if (percentage <= 30) return <SeverityError width={"12px"} />;
  };

  const { trackEvent } = useAPI();

  const openTechniqueUrl = (item) => {
    trackEvent("Clicked on MITRE ATT&CK Technique: " + item);
    const formattedItem = item.includes(".") ? item.replace(".", "/") : item;
    window.open(
      `https://attack.mitre.org/techniques/${formattedItem}`,
      "_blank"
    );
  };

  const openAttackGroupeUrl = (item) => {
    trackEvent("Clicked on MITRE ATT&CK Actor: " + item.name);
    window.open(
      "https://attack.mitre.org/groups/" + item.external_id,
      "_blank"
    );
  };

  return (
    <Box>
      <Box
        style={{
          display: "flex",
          justifyContent: "space-between",
          maxWidth: "100%",
        }}
      >
        <Typography
          variant="overline"
          style={{ flexGrow: 1, whiteSpace: "normal", lineHeight: "18px" }}
        >
          {technique.name}
        </Typography>
        <PercentageLabel
          percentage={technique.percentage}
          applicable={technique.applicable}
          negativePercentage={showRisk}
        />
      </Box>
      <Divider sx={{ marginY: 1 }} />
      <Box sx={{ marginTop: "0px" }}>
        {technique.applicable && (
          <>
            <Typography variant="overline" sx={{ color: colors.grey_dark }}>
              Threat Type
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "flex-start",
                marginTop: "4px",
                marginBottom: "4px",
                gap: 0.5,
              }}
            >
              {technique.related_threat_types?.map((threatType) => (
                <CustomLabel
                  text={threatType}
                  colorFamily="secondary"
                  severity="dark"
                  labelType="soft"
                  size="small"
                />
              ))}
            </Box>
            <Typography variant="overline" sx={{ color: colors.grey_dark }}>
              Threat Actors
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "flex-start",
                marginTop: "4px",
                marginBottom: "4px",

                gap: 0.5,
              }}
            >
              {technique.related_attack_groups
                ?.slice(0, 3)
                .map((attackActor) => (
                  <CustomLabel
                    text={attackActor.name}
                    colorFamily="secondary"
                    severity="dark"
                    labelType="soft"
                    size="small"
                    onClick={() => openAttackGroupeUrl(attackActor)}
                  />
                ))}
            </Box>
            <Typography
              variant="overline"
              sx={{ color: colors.grey_dark, marginBottom: "8px" }}
            >
              Source
            </Typography>
            <Box sx={{ marginBottom: "4px" }}>
              <CustomLabel
                text={technique.id}
                colorFamily="secondary"
                severity="dark"
                labelType="soft"
                size="small"
                onClick={() => openTechniqueUrl(technique.id)}
                icon={<LaunchIconRounded sx={{ height: "16px" }} />}
              />
            </Box>

            {technique.defense_performance && (
              <Typography variant="overline" sx={{ color: colors.grey_dark }}>
                Defense Performance
              </Typography>
            )}
            {technique.defense_performance &&
              Object.entries(technique.defense_performance).map(
                ([tool, { total, compliant, percentage }]) => (
                  <Grid container spacing={2} key={tool} alignItems="center">
                    {/* Tool Logo */}
                    <Grid item sx={{ marginBottom: "-8px" }}>
                      <SvgToIcon
                        IconLogo={IntegrationsMapping[tool.toLowerCase()].logo}
                        width={26}
                        height={26}
                      />
                    </Grid>
                    {/* How many compliant */}
                    <Grid
                      item
                      sx={{
                        flexGrow: 1,
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          maxWidth: "100%",
                        }}
                      >
                        <Box
                          style={{
                            display: "flex",
                            justifyContent: "flex-start",
                            flexGrow: 1,
                            alignItems: "center",
                          }}
                        >
                          <Box
                            style={{
                              minWidth: "40px",
                            }}
                          >
                            <Typography
                              variant="caption"
                              style={{
                                color: colors.grey_dark,
                              }}
                            >
                              {compliant}/{total}
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            style={{
                              whiteSpace: "normal",
                              color: colors.grey_dark,
                            }}
                          >
                            compliant
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    {/* Compliant Icon */}
                    <Grid item sx={{ flex: "0 1 auto", marginBottom: "-5px" }}>
                      {getCompliantIcon(percentage)}
                    </Grid>
                  </Grid>
                )
              )}
          </>
        )}
      </Box>

      {!technique.applicable && (
        <Typography variant="overline" sx={{ color: colors.grey_dark }}>
          Currently Not Covered by Breeze
        </Typography>
      )}
    </Box>
  );
};

export default TechniqueTooltipContent;
